import dayjs from 'dayjs';
import 'dayjs/locale/zh-cn';
import { Calendar } from 'antd';
import type { Dayjs } from 'dayjs';
import dayLocaleData from 'dayjs/plugin/localeData';
import ArrowLeft from '../../assets/images/blueArrowLeft.svg';
import ArrowRight from '../../assets/images/blueRightArrow.svg';
import { useDispatch, useSelector } from 'react-redux';
import { selectSelectedDate, setSelectedDate } from './slice/calendarSlice';
import { useRef } from 'react';

dayjs.extend(dayLocaleData);

type CustomCalendarProps = {
    disabledDates?: Set<string>;
    className?: string;
};

const CustomCalendar = ({ disabledDates, className }: CustomCalendarProps) => {
    const dispatch = useDispatch();
    const selectedDate = useSelector(selectSelectedDate)
    const suppressSelect = useRef(false);

    const disabledDate = (current: Dayjs): boolean => {
        const isPast = current.isBefore(dayjs().startOf('day'), 'day');
        const isWeekend = current.day() === 0 || current.day() === 6; // Sunday or Saturday
        const isBlocked = disabledDates?.has(current.format('YYYY-MM-DD')) ?? false;
        return isPast || isWeekend || isBlocked;
    };

    const onSelect = (date: Dayjs) => {
        if (date.day() === 0 || date.day() === 6) dispatch(setSelectedDate(''))
        else if (suppressSelect.current && !date.isSame(dayjs(), 'month')) {
            dispatch(setSelectedDate(''));
            suppressSelect.current = false;
            return;
        }
        else dispatch(setSelectedDate(date.format('YYYY-MM-DD')));
    };

    const onPanelChange = () => {
        dispatch(setSelectedDate(''));
        suppressSelect.current = true; 
    };

    return (
        <div className={`flex items-center justify-center w-full calendarContainer ${className ?? ''}`}>
            <Calendar
                fullscreen={false}
                defaultValue={selectedDate ? dayjs(selectedDate) : dayjs()}
                disabledDate={disabledDate}
                onSelect={onSelect}
                onPanelChange={onPanelChange}
                fullCellRender={(date) => (
                    <div key={date.format('YYYY-MM-DD')} className={`ant-picker-cell-inner ${selectedDate === date.format('YYYY-MM-DD') ? 'bg-indigo-700 text-white' : !disabledDate(date) ? '!bg-transparent !text-indigo-700 [&>.ant-picker-cell-selected]:!bg-transparent inActiveDate' : ''}`}>
                        {date.date()}
                    </div>
                )}
                headerRender={({ value, onChange }) => {
                    const current = value;
                    const today = dayjs();

                    const isPrevDisabled =
                        current.year() < today.year() ||
                        (current.year() === today.year() && current.month() <= today.month());

                    const prevMonth = () => {
                        if (!isPrevDisabled) {
                            const newValue = current.clone().subtract(1, 'month');
                            onChange(newValue);
                        }
                    };

                    const nextMonth = () => {
                        const newValue = current.clone().add(1, 'month');
                        onChange(newValue);
                    };

                    return (
                        <div className='flex items-center justify-between'>
                            <img src={ArrowLeft} alt='<-' className={`${isPrevDisabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'} `} onClick={prevMonth} />
                            <span className='text-lg font-medium text-gray-900'>{current.format('MMMM YYYY')}</span>
                            <img src={ArrowRight} alt='->' className='cursor-pointer' onClick={nextMonth} />
                        </div>
                    );
                }}
            />
        </div>
    );
};

export default CustomCalendar;
